import { useState, useEffect, useCallback } from 'react';
import { draftService, DraftData } from '../services/draftService';

export interface Draft {
  id: string;
  name: string;
  description: string;
  technicalDetails: string;
  techKeywords: string[];
  isBuilt: boolean;
  preferences: any;
  updatedAt?: string;
  createdAt?: string;
}

export const useDrafts = () => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await draftService.getDrafts();
      if (result.success) {
        setDrafts(result.data);
      }
    } catch (e) {
      console.error('Failed to fetch drafts', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const saveDraft = useCallback(async (draftData: Omit<Draft, 'id' | 'updatedAt' | 'createdAt'>, id?: string) => {
    try {
      const payload: DraftData = {
        ...draftData,
        id
      };
      const result = await draftService.saveDraft(payload);
      if (result.success && result.data) {
        setDrafts(prev =>
          prev.some(d => d.id === result.data.id)
            ? prev.map(d => d.id === result.data.id ? result.data : d)
            : [result.data, ...prev]
        );
        return result.data.id;
      }
    } catch (e) {
      console.error('Failed to save draft', e);
      throw e;
    }
  }, []);

  const deleteDraft = useCallback(async (id: string) => {
    try {
      const result = await draftService.deleteDraft(id);
      if (result.success) {
        setDrafts(prev => prev.filter(d => d.id !== id));
      }
    } catch (e) {
      console.error('Failed to delete draft', e);
      throw e;
    }
  }, []);

  return { drafts, saveDraft, deleteDraft, loading, fetchDrafts };
};
